import { useParams, Link } from 'react-router-dom';
import { useOrgCredentials } from '@/hooks/useIdentityCredentials';
import { AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ProviderLogo,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  getStatusVariant,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useSpaceId } from '@/contexts/SpaceContext';
import { useAuth } from '@/store/auth-context';
import { routes } from '@/lib/routes';
import { useOrgMcpServer } from '@/hooks/useOrgMcpServers';
import {
  useInvalidateOrgMcpAuthStatus,
  useOrgMcpAuthStatus,
} from '@/hooks/useOrgMcpAuthStatus';
import { useMcpAuthEvents } from '@/hooks/useMcpAuthEvents';
import { useMcpProviderById } from '@/hooks/useMcpProviders';
import { deriveMcpStatus } from '@/types/mcp';
import BackLink from '@/components/navigation/BackLink';
import { CimdClientBadge } from '@/pages/tools/mcp/CimdClientBadge';

export default function OrgMcpServerView() {
  const { serverId } = useParams<{ serverId: string }>();
  const spaceId = useSpaceId();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { success, error: showError } = useToast();

  const returnUrl = `${window.location.origin}/oauth/connected`;

  const { data: server, isLoading } = useOrgMcpServer(serverId);
  // The OAuth app lives in optr-identity (a papp row keyed by serverId), not
  // on the agent-core server config — the credentials response is the only
  // place that knows which client is wired up.
  const { data: credentials } = useOrgCredentials();
  const oauthApp = credentials?.providers.find(
    (p) => p.providerInstanceId === serverId,
  );
  const catalogEntry = useMcpProviderById(server?.providerId);
  const { data: auth, refetch: refetchAuth } = useOrgMcpAuthStatus(
    serverId,
    returnUrl,
  );
  const invalidateAuth = useInvalidateOrgMcpAuthStatus();

  useMcpAuthEvents({
    serverId,
    onConnected: (id) => {
      success('Connected to MCP server');
      invalidateAuth(id);
    },
    onFailed: (_id, message) => {
      showError(message ?? 'Connection failed');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!server) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Organization MCP server not found.{' '}
        <Link
          to={routes.console.spaceToolsMcpServers(spaceId)}
          className="text-primary underline"
        >
          Back to MCP servers
        </Link>
      </div>
    );
  }

  // Stash the (orgId, serverId) pair so /oauth/connected can name the server.
  const openAuthorizationUrl = (url: string) => {
    try {
      localStorage.setItem(
        'optr.oauth.pending',
        JSON.stringify({
          orgId,
          serverId: server.serverId,
          ts: Date.now(),
        }),
      );
    } catch {
      // localStorage disabled — landing page falls back to generic copy.
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleConnect = () => {
    if (auth?.authorizationUrl) openAuthorizationUrl(auth.authorizationUrl);
  };

  const handleCheckAgain = () => {
    invalidateAuth(server.serverId);
    refetchAuth();
  };

  return (
    <div className="space-y-6">
      <BackLink to={routes.console.spaceToolsMcpServers(spaceId)}>MCP servers</BackLink>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {server.providerId && (
            <ProviderLogo
              slug={server.providerId}
              displayName={catalogEntry?.displayName}
              className="h-12 w-12"
              fallbackClassName="text-sm"
            />
          )}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{server.name}</h1>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="info" className="cursor-default">From org</Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs">
                  Managed by org admins
                </TooltipContent>
              </Tooltip>
            </div>
            {catalogEntry && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {catalogEntry.displayName}
              </p>
            )}
          </div>
        </div>
        {(() => {
          const status = deriveMcpStatus(server);
          return <Badge variant={getStatusVariant(status)}>{status}</Badge>;
        })()}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader title="Configuration" />
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Server URL
              </dt>
              <dd className="mt-0.5 text-sm text-foreground break-all">
                {server.serverUrl}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Default group
              </dt>
              <dd className="mt-0.5 text-sm text-foreground">
                {server.defaultGroup || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Scopes</dt>
              <dd className="mt-0.5 text-sm text-foreground">
                {server.scopes && server.scopes.length > 0
                  ? server.scopes.join(', ')
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                OAuth client
              </dt>
              <dd className="mt-0.5 text-sm text-foreground">
                {oauthApp?.oauthApp ? (
                  <>
                    <span className="font-mono text-xs">
                      {oauthApp.oauthApp.clientId}
                    </span>
                    {!oauthApp.oauthApp.hasClientSecret && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        public client — no secret
                      </span>
                    )}
                  </>
                ) : server.oauthClientMode === 'cimd' ? (
                  <CimdClientBadge />
                ) : (
                  'Not configured'
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Server ID
              </dt>
              <dd className="mt-0.5 text-sm text-foreground font-mono text-xs">
                {server.serverId}
              </dd>
            </div>
            {server.lastError && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">
                  Last error
                </dt>
                <dd className="mt-0.5 text-sm text-red-600 dark:text-red-400">
                  {server.lastError}
                </dd>
              </div>
            )}
          </dl>
        </Card>

        <Card>
          <CardHeader
            title="Service account"
            description="Organization-level fallback credential — used as Tier 3 when a calling user has no personal token."
          />
          {server.serviceAccount?.configured ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Configured</span>
              </div>
              {server.serviceAccount.scopes &&
                server.serviceAccount.scopes.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    Scopes: {server.serviceAccount.scopes.join(', ')}
                  </p>
                )}
              {server.serviceAccount.lastError && (
                <div className="flex gap-2 text-sm text-yellow-700 dark:text-yellow-300">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>{server.serviceAccount.lastError}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No service-account credential set. Tool calls fall back to
              per-user OAuth only.
            </p>
          )}
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Your connection"
          description="Connect with your own credentials. Agents you own use your personal connection before falling back to the organization service account."
        />
        {auth?.authorized ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Connected</span>
            </div>
            {auth.scopes && auth.scopes.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Scopes: {auth.scopes.join(', ')}
              </p>
            )}
            {auth.expiresAt && (
              <p className="text-xs text-muted-foreground">
                Renews automatically • token expires{' '}
                {new Date(auth.expiresAt).toLocaleString()}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {auth?.lastError && (
              <div className="flex gap-2 text-sm text-yellow-700 dark:text-yellow-300">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <p>{auth.lastError}</p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              The authorization page opens in a new tab.
            </p>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleConnect}
                disabled={!auth?.authorizationUrl}
              >
                Connect {server.name}
                <ExternalLink className="h-3.5 w-3.5 ml-2" />
              </Button>
              <Button variant="outline" onClick={handleCheckAgain}>
                Check again
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Tools"
          description="Tools synced from this server are available in every space in the organization."
        />
        {server.lastSyncedAt ? (
          <p className="text-sm text-muted-foreground">
            Last synced {new Date(server.lastSyncedAt).toLocaleString()}
            {typeof server.toolCount === 'number' &&
              ` · ${server.toolCount} tool${server.toolCount === 1 ? '' : 's'}`}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Not synced yet.</p>
        )}
      </Card>
    </div>
  );
}
